import * as React from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  IconButton,
  Pagination,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import dayjs from 'dayjs';

const API_URL = import.meta.env.VITE_API_URL;

interface Log {
  logId: number;
  author: string;
  source: string;
  sourceLink?: string;
  action: 'CREATE' | 'UPDATE' | 'DELETE' | 'ELSE' | 'ERROR';
  createdAt: string;
  jsonData?: any;
  description?: string;
  stack?: string;
  sourceInfo?: string;
}

export default function History() {
  const [logs, setLogs] = React.useState<Log[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [page, setPage] = React.useState(1);
  const [pages, setPages] = React.useState(1);
  const [limit] = React.useState(10);
  const [search, setSearch] = React.useState('');
  const [actionFilter, setActionFilter] = React.useState<string>('all');
  const [selectedLog, setSelectedLog] = React.useState<Log | null>(null);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params: any = { page, limit };
      if (search) params.source = search;
      if (actionFilter !== 'all') params.action = actionFilter;

      const { data } = await axios.get(`${API_URL}/logs`, { params });

      setLogs(data.logs || []);
      setPages(Math.ceil((data.count || data.logs.length) / limit));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchLogs();
  }, [page, search, actionFilter]);

  const handleDelete = async (logId: number) => {
    if (!confirm('Видалити цей лог?')) return;
    try {
      await axios.delete(`${API_URL}/logs/${logId}`);
      fetchLogs();
    } catch (err) {
      console.error(err);
    }
  };

  const handleClearAll = async () => {
    if (!confirm('Ви впевнені, що хочете видалити всі логи?')) return;

    try {
      await axios.delete(`${API_URL}/logs-clear-all`);
      window.location.reload();
    } catch (e: any) {
      alert('Помилка при видаленні логів: ' + e.message);
    }
  };

  const renderAction = (action: string) => {
    let color = '#888';
    let label = 'Інше';

    switch (action) {
      case 'CREATE':
        color = '#2196f3';
        label = 'Створення';
        break;
      case 'UPDATE':
        color = '#4caf50';
        label = 'Оновлення';
        break;
      case 'DELETE':
        color = '#f44336';
        label = 'Видалення';
        break;
      case 'ERROR':
        color = '#d32f2f';
        label = 'Помилка';
        break;
    }

    return <span style={{ color, fontWeight: 'bold' }}>{label}</span>;
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Історія дій
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Box style={{ fontStyle: 'italic' }}>
          Історія зберігається за останні 30 днів
        </Box>

        <TextField
          label="Пошук по типу (продукція, категорія, тощо).."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />

        <Select
          value={actionFilter}
          onChange={(e) => {
            setActionFilter(e.target.value);
            setPage(1);
          }}
        >
          <MenuItem value="all">Всі дії</MenuItem>
          <MenuItem value="CREATE">Створення</MenuItem>
          <MenuItem value="UPDATE">Оновлення</MenuItem>
          <MenuItem value="DELETE">Видалення</MenuItem>
          <MenuItem value="ERROR">Помилка</MenuItem>
          <MenuItem value="ELSE">Інше</MenuItem>
        </Select>

        <Button color="error" variant="outlined" onClick={handleClearAll}>
          Очистити всі логи
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Час</TableCell>
              <TableCell>Автор</TableCell>
              <TableCell>Тип</TableCell>
              <TableCell>Дія</TableCell>
              <TableCell>Керування</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  Завантаження...
                </TableCell>
              </TableRow>
            ) : logs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  Немає логів
                </TableCell>
              </TableRow>
            ) : (
              logs.map((log) => (
                <TableRow key={log.logId}>
                  <TableCell>
                    {dayjs(log.createdAt).format('DD-MM-YYYY HH:mm')}
                  </TableCell>

                  <TableCell>{log.author}</TableCell>

                  <TableCell>{log.source}</TableCell>

                  <TableCell>{renderAction(log.action)}</TableCell>

                  <TableCell>
                    {log.sourceLink && (
                      <IconButton
                        component="a"
                        href={log.sourceLink}
                        target="_blank"
                      >
                        <OpenInNewIcon />
                      </IconButton>
                    )}

                    {log.action === 'ERROR' && (
                      <IconButton onClick={() => setSelectedLog(log)}>
                        <InfoIcon />
                      </IconButton>
                    )}

                    <IconButton onClick={() => handleDelete(log.logId)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {pages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            page={page}
            count={pages}
            onChange={(_, v) => setPage(v)}
          />
        </Box>
      )}

      <Dialog
        open={!!selectedLog}
        onClose={() => setSelectedLog(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Деталі помилки</DialogTitle>

        <DialogContent dividers>
          <Typography gutterBottom>
            <b>Опис:</b> {selectedLog?.description}
          </Typography>

          <Typography gutterBottom>
            <b>Джерело:</b> {selectedLog?.source}
          </Typography>

          <Typography gutterBottom>
            <b>Додаткова інформація:</b> {selectedLog?.sourceInfo}
          </Typography>

          {selectedLog?.jsonData && (
            <Box sx={{ mt: 2 }}>
              <Typography><b>JSON:</b></Typography>
              <pre>{JSON.stringify(selectedLog.jsonData, null, 2)}</pre>
            </Box>
          )}

          {selectedLog?.stack && (
            <Box sx={{ mt: 2 }}>
              <Typography><b>Stack trace:</b></Typography>
              <pre>{selectedLog.stack}</pre>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setSelectedLog(null)}>Закрити</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}